import React, { useEffect, useState } from 'react'
import { ColumnsType } from 'antd/lib/table'
import { useNavigate, useParams } from 'react-router-dom'
import { IconButton } from '../../shared/components/buttons/icon-button/IconButton'
import { IconArrowBack } from '../../shared/components/icons/IconArrowBack'
import { useAppSelector } from '../../app/store/hooks/Redux'
import { selectKeywordsFilter } from '../advertising-research/model/keywordsSlice'
import { IKeywordsFilter } from '../advertising-research/model/types/KeywordsFilter.interface'
import { selectResearchFilters } from '../advertising-research/model/researchSlice'
import { IResearchFilter } from '../advertising-research/model/types/ResearchFilter.interface'
import { TTableResponse } from '../../shared/types/TableResponse.type'
import { BpTable } from '../../shared/components/cards/table/BpTable'
import { IBpPaginationProps } from '../../shared/components/cards/pagination/BpPagination'
import { SorterResult } from 'antd/lib/table/interface'
import { useSorter } from '../../shared/lib/hooks/UseSorter'
import { ExportDrop } from '../../shared/components/cards/export-drop/ExportDrop'
import { useTranslation } from 'react-i18next'
import {
  GetResearchKeywordsColumnAdvertisersApiArg,
  ResearchColumnArg,
  ResearchKeywordsColumnAdvertisersItem,
  useExportAdvertisers,
  useGetResearchKeywordsColumnAdvertisersQuery,
} from '../../app/api'
import { AdvertiserDomainCell } from './ui/cells/AdvertiserDomainCell'
import clsx from 'clsx'

import styles from './AdvertisersPage.module.scss'

export const AdvertisersPage = () => {
  const { t } = useTranslation()
  const { id, keyword } = useParams()
  const navigate = useNavigate()
  const [countRows, setCountRows] = useState<number>(0)
  const {
    dateRange: { date },
  } = useAppSelector<IKeywordsFilter>(selectKeywordsFilter)
  const { country, domain } = useAppSelector<IResearchFilter>(selectResearchFilters)
  const [queryParams, setQueryParams] = useState<
    ResearchColumnArg<GetResearchKeywordsColumnAdvertisersApiArg>
  >({
    page: 1,
    'per-page': 10,
    projectId: (() => {
      if (id) return Number.parseInt(id)
    })(),
    date,
    'country_id[]': country,
    'domain[]': domain ? domain.split(',') : undefined,
    keyword,
  })

  const { data, isLoading, isFetching } = useGetResearchKeywordsColumnAdvertisersQuery(
    queryParams as GetResearchKeywordsColumnAdvertisersApiArg,
    { skip: keyword == undefined },
  )
  const [exportFile, { isFetching: exportAdvertiserLoading }] = useExportAdvertisers()

  useEffect(() => {
    if (!data?.data) return
    setCountRows(data.data.total)
  }, [data])

  const columns: ColumnsType<ResearchKeywordsColumnAdvertisersItem> = [
    {
      title: t('table.columns.domain'),
      dataIndex: 'finish_domain',
      key: 'finish_domain',
      render: (_: string, record) => <AdvertiserDomainCell {...record} />,
    },
    {
      title: t('table.columns.ads'),
      dataIndex: 'ads',
      key: 'ads',
      width: 150,
    },
    {
      title: t('table.columns.firstFound'),
      dataIndex: 'first_found',
      key: 'first_found',
      width: 150,
    },
    {
      title: t('table.columns.lastFound'),
      dataIndex: 'last_found',
      key: 'last_found',
      width: 150,
      sorter: true,
    },
  ]

  const handleTableChange = (
    { current, pageSize }: IBpPaginationProps,
    sorter?:
      | SorterResult<ResearchKeywordsColumnAdvertisersItem>
      | SorterResult<ResearchKeywordsColumnAdvertisersItem>[],
  ) => {
    setQueryParams({ ...queryParams, page: current, 'per-page': pageSize, sort: useSorter(sorter) })
  }

  return (
    <section className={clsx(styles.wrapper)}>
      <div className={clsx(styles.header)}>
        <span className={clsx(styles.titleRow)}>
          <IconButton size='lg' onClick={() => navigate(-1)}>
            <IconArrowBack color='#00326D' />
          </IconButton>
          <h3 className={clsx(styles.title)}>
            {countRows} {t('pages.advertisingResearch.advertisersFor')} “{keyword}”
          </h3>
        </span>
        <ExportDrop
          columns={[
            { label: t('table.columns.domain'), keys: ['finish_domain'] },
            { label: t('table.columns.ads'), keys: ['ads'] },
            { label: t('table.columns.firstFound'), keys: ['first_found'] },
            { label: t('table.columns.lastFound'), keys: ['last_found'] },
          ]}
          loading={exportAdvertiserLoading}
          onExport={(exportParams) => exportFile({ ...queryParams, ...exportParams })}
        />
      </div>
      <BpTable
        columns={columns}
        data={data as TTableResponse<ResearchKeywordsColumnAdvertisersItem>}
        dataSource={data?.data?.items}
        loading={isLoading || isFetching}
        rowKey={({ finish_domain, first_found }) => `${finish_domain}${first_found}`}
        scroll={{ x: '896px' }}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
      ></BpTable>
    </section>
  )
}
